import { useEffect, useState } from "react";
import React from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { AIThinkingState } from "@/components/AltioraComponents";
import { toast } from "sonner";
import {
  Wand2, Briefcase, Mic, Plane, GraduationCap, Rocket, FileBarChart,
  Download, RefreshCw, X, Clock, Sparkles, Trash2, Palette, Upload,
} from "lucide-react";

const FAMILY_ICON = {
  emploi: Briefcase, entrevues: Mic, immigration: Plane,
  formation: GraduationCap, entrepreneuriat: Rocket, rapports: FileBarChart,
};

// Reuse the markdown renderer used in Library
function renderMarkdown(md) {
  const lines = (md || "").split("\n");
  const out = []; let i = 0; let key = 0;
  const kget = () => `md-${key++}`;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("```")) {
      const code = []; i++;
      while (i < lines.length && !lines[i].startsWith("```")) { code.push(lines[i]); i++; }
      i++;
      out.push(<pre key={kget()} className="bg-[#F1EFE9] rounded-md p-3 text-xs font-mono text-[#1F2937] overflow-x-auto my-3"><code>{code.join("\n")}</code></pre>); continue;
    }
    if (line.startsWith("# ")) { out.push(<h1 key={kget()} className="text-2xl font-display text-[#0B2545] mt-6 mb-3">{line.slice(2)}</h1>); i++; continue; }
    if (line.startsWith("## ")) { out.push(<h2 key={kget()} className="text-lg font-display text-[#0B2545] mt-5 mb-2">{line.slice(3)}</h2>); i++; continue; }
    if (line.startsWith("### ")) { out.push(<h3 key={kget()} className="text-sm font-medium text-[#1F2937] mt-4 mb-1.5">{line.slice(4)}</h3>); i++; continue; }
    if (line.startsWith("> ")) { out.push(<blockquote key={kget()} className="border-l-2 border-[#C5A880] pl-3 my-3 italic text-[#7A5B14] text-sm">{renderInline(line.slice(2))}</blockquote>); i++; continue; }
    if (line.match(/^\s*[-*] /)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\s*[-*] /)) {
        items.push(<li key={kget()} className="mb-1">{renderInline(lines[i].replace(/^\s*[-*] /, ""))}</li>); i++;
      }
      out.push(<ul key={kget()} className="list-disc list-inside text-sm text-[#1F2937] space-y-0.5 my-3 pl-2">{items}</ul>); continue;
    }
    if (line.match(/^\s*\d+\. /)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\s*\d+\. /)) {
        items.push(<li key={kget()} className="mb-1">{renderInline(lines[i].replace(/^\s*\d+\.\s*/, ""))}</li>); i++;
      }
      out.push(<ol key={kget()} className="list-decimal list-inside text-sm text-[#1F2937] space-y-0.5 my-3 pl-2">{items}</ol>); continue;
    }
    if (line === "---") { out.push(<hr key={kget()} className="my-4 border-[#E4E1D8]" />); i++; continue; }
    if (line.trim() === "") { out.push(<div key={kget()} className="h-2" />); i++; continue; }
    out.push(<p key={kget()} className="text-sm text-[#1F2937] leading-relaxed my-2">{renderInline(line)}</p>); i++;
  }
  return out;
}
function renderInline(text) {
  const parts = []; let key = 0;
  const kget = () => `inl-${key++}`;
  let rem = text;
  const rx = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/;
  while (rem) {
    const m = rem.match(rx);
    if (!m) { parts.push(<span key={kget()}>{rem}</span>); break; }
    if (m.index > 0) parts.push(<span key={kget()}>{rem.slice(0, m.index)}</span>);
    const t = m[0];
    if (t.startsWith("**")) parts.push(<strong key={kget()} className="text-[#0B2545]">{t.slice(2, -2)}</strong>);
    else if (t.startsWith("*")) parts.push(<em key={kget()}>{t.slice(1, -1)}</em>);
    else parts.push(<code key={kget()} className="bg-[#F1EFE9] px-1 rounded text-xs">{t.slice(1, -1)}</code>);
    rem = rem.slice(m.index + t.length);
  }
  return <>{parts}</>;
}

export default function Studio() {
  useI18n();
  const [meta, setMeta] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [currentFamily, setCurrentFamily] = useState(null);
  const [wizard, setWizard] = useState(null); // { doc_type_meta }
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [f, docs] = await Promise.all([
          api.get("/studio/families"),
          api.get("/studio/documents?limit=50"),
        ]);
        setMeta(f.data);
        setDocuments(docs.data.items || []);
      } catch { toast.error("Chargement Studio impossible"); }
      finally { setLoading(false); }
    })();
  }, []);

  const refresh = async () => {
    const docs = await api.get("/studio/documents?limit=50");
    setDocuments(docs.data.items || []);
  };

  const openDoc = async (id) => {
    const r = await api.get(`/studio/documents/${id}`);
    setSelected(r.data);
  };

  const downloadPdf = async (doc) => {
    // Legacy — kept for direct button, delegates to unified export
    return downloadFormat(doc, "pdf");
  };

  const downloadFormat = async (doc, fmt) => {
    try {
      const r = await api.get(`/studio/documents/${doc.id}/export/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const base = (doc.title_fr || doc.label_fr || "document").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      a.download = `${base}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} téléchargé`);
    } catch (e) {
      const msg = e.response?.status === 422
        ? `Format ${fmt.toUpperCase()} non supporté pour ce document`
        : `Échec téléchargement ${fmt.toUpperCase()}`;
      toast.error(msg);
    }
  };

  const filtered = currentFamily
    ? (meta?.document_types || []).filter((t) => t.family === currentFamily)
    : (meta?.document_types || []);

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="studio-root">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-3">
              <Wand2 className="h-5 w-5 text-[#C5A880]" strokeWidth={2} />
              <div className="text-xs font-mono uppercase tracking-[0.28em] text-navy">ALTIORA Studio™</div>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-navy leading-[1.05]">
              Atelier de production documentaire
            </h1>
            <p className="text-base md:text-lg text-[#374151] mt-5 max-w-2xl leading-relaxed">
              38 documents intelligents générés à partir de votre profil, de vos objectifs et des opportunités détectées.
              De la découverte à la concrétisation, en quelques secondes.
            </p>
          </div>
        </div>

        {/* Family chips */}
        <div className="mb-5 flex items-center gap-2 flex-wrap" data-testid="studio-families">
          <button
            onClick={() => setCurrentFamily(null)}
            className={`px-3 py-1.5 text-sm rounded-full border ${!currentFamily ? "bg-[#0B2545] text-white border-[#0B2545]" : "bg-white text-[#1F2937] border-[#E4E1D8] hover:border-[#0B2545]"}`}
          >
            Tous (38)
          </button>
          {(meta?.families || []).map((f) => {
            const Icon = FAMILY_ICON[f.key] || Wand2;
            const active = currentFamily === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setCurrentFamily(f.key)}
                data-testid={`studio-family-${f.key}`}
                className={`px-3 py-1.5 text-sm rounded-full border inline-flex items-center gap-1.5 ${active ? "bg-[#0B2545] text-white border-[#0B2545]" : "bg-white text-[#1F2937] border-[#E4E1D8] hover:border-[#0B2545]"}`}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={1.5} /> {f.label_fr}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Left: catalog */}
          <div className="col-span-12 md:col-span-7">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62] mb-2">
              Générer un document ({filtered.length})
            </div>
            {loading ? <AIThinkingState label="Chargement…" /> : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="studio-catalog">
                {filtered.map((t) => {
                  const Icon = FAMILY_ICON[t.family] || Wand2;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setWizard({ typeMeta: t })}
                      className="text-left p-4 bg-white border border-[#E4E1D8] rounded-lg hover:border-[#0B2545] hover:shadow-md transition"
                      data-testid={`studio-type-${t.key}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-md bg-[#FBF6EA] flex items-center justify-center flex-shrink-0">
                          <Icon className="h-4 w-4 text-[#7A5B14]" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-mono uppercase tracking-wide text-[#8B8B8F]">
                            {meta?.families.find((f) => f.key === t.family)?.label_fr}
                          </div>
                          <h3 className="text-sm font-display font-medium text-[#1F2937] tracking-tight mt-0.5">{t.label_fr}</h3>
                          <p className="text-[11px] text-[#5B5B62] mt-1 line-clamp-2">{t.prompt_focus}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right: my documents */}
          <div className="col-span-12 md:col-span-5">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62] mb-2">
              Mes documents ({documents.length})
            </div>
            <div className="bg-white border border-[#E4E1D8] rounded-lg divide-y divide-[#F1EFE9] max-h-[600px] overflow-y-auto" data-testid="studio-my-docs">
              {documents.length === 0 ? (
                <div className="p-6 text-center text-sm text-[#8B8B8F]">Aucun document généré pour l&apos;instant.</div>
              ) : documents.map((d) => {
                const Icon = FAMILY_ICON[d.family] || Wand2;
                return (
                  <button
                    key={d.id}
                    onClick={() => openDoc(d.id)}
                    className="w-full text-left px-4 py-3 hover:bg-[#F1EFE9] transition"
                    data-testid="studio-doc-item"
                  >
                    <div className="flex items-start gap-3">
                      <Icon className="h-4 w-4 text-[#0B2545] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#1F2937] truncate">{d.title_fr}</div>
                        <div className="text-[10px] text-[#8B8B8F] mt-0.5">
                          {d.label_fr} · {new Date(d.created_at).toLocaleDateString("fr-CA")}
                          {d.source_context?.target_company && ` · ${d.source_context.target_company}`}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {wizard && <GenerateWizard typeMeta={wizard.typeMeta} onClose={() => setWizard(null)} onGenerated={(d) => { setWizard(null); setSelected(d); refresh(); }} />}
        {selected && <DocViewer doc={selected} onClose={() => setSelected(null)} onDownload={downloadPdf} onDownloadFormat={downloadFormat} onRefresh={() => { refresh(); setSelected(null); }} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wizard
// ---------------------------------------------------------------------------
function GenerateWizard({ typeMeta, onClose, onGenerated }) {
  const [form, setForm] = useState({
    doc_type: typeMeta.key, opportunity_id: "", journey_id: "",
    target_company: "", target_role: "", target_program: "",
    extra_context_fr: "", tone: "professionnel",
  });
  const [busy, setBusy] = useState(false);
  const [ops, setOps] = useState([]);
  const [journeys, setJourneys] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [o, j] = await Promise.all([
          api.get("/discovery/feed?limit=15"),
          api.get("/knowledge/journeys?limit=10"),
        ]);
        setOps(o.data.items || []);
        setJourneys(j.data.items || []);
      } catch { /* ok */ }
    })();
  }, []);
  const gen = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      const r = await api.post("/studio/generate", payload);
      toast.success(`Document généré par ${r.data.llm_used || "IA"}`);
      onGenerated(r.data);
    } catch { toast.error("Échec de la génération"); }
    finally { setBusy(false); }
  };
  const inputCls = "w-full px-2.5 py-1.5 text-sm bg-white border border-[#E4E1D8] rounded-md focus:outline-none focus:border-[#0B2545]";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="studio-wizard-modal">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62]">Studio · Génération</div>
            <h3 className="text-lg font-display text-[#1F2937]">{typeMeta.label_fr}</h3>
          </div>
          <button onClick={onClose} className="p-1 text-[#5B5B62]"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <div className="text-xs text-[#5B5B62]">{typeMeta.prompt_focus}</div>
          <Field label="Poste ciblé"><input className={inputCls} value={form.target_role} onChange={(e) => setForm({ ...form, target_role: e.target.value })} placeholder="Ex : Analyste SOC" data-testid="wizard-target-role" /></Field>
          <Field label="Entreprise ciblée"><input className={inputCls} value={form.target_company} onChange={(e) => setForm({ ...form, target_company: e.target.value })} placeholder="Ex : Desjardins" /></Field>
          <Field label="Programme ciblé (formation/immigration)"><input className={inputCls} value={form.target_program} onChange={(e) => setForm({ ...form, target_program: e.target.value })} placeholder="Ex : Arrima" /></Field>
          <Field label="Opportunité liée (optionnel)">
            <select className={inputCls} value={form.opportunity_id} onChange={(e) => setForm({ ...form, opportunity_id: e.target.value })}>
              <option value="">— Aucune —</option>
              {ops.slice(0, 10).map((o) => <option key={o.id} value={o.id}>{o.title_fr?.slice(0, 60)}</option>)}
            </select>
          </Field>
          <Field label="Parcours lié (optionnel)">
            <select className={inputCls} value={form.journey_id} onChange={(e) => setForm({ ...form, journey_id: e.target.value })}>
              <option value="">— Aucun —</option>
              {journeys.map((j) => <option key={j.id} value={j.id}>{j.goal_fr?.slice(0, 60)}</option>)}
            </select>
          </Field>
          <Field label="Ton">
            <select className={inputCls} value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })}>
              <option value="professionnel">Professionnel</option>
              <option value="chaleureux">Chaleureux</option>
              <option value="direct">Direct</option>
              <option value="académique">Académique</option>
            </select>
          </Field>
          <Field label="Contexte additionnel"><textarea className={inputCls + " min-h-[70px]"} value={form.extra_context_fr} onChange={(e) => setForm({ ...form, extra_context_fr: e.target.value })} placeholder="Détails, contraintes, informations spécifiques…" /></Field>
        </div>
        <div className="px-6 py-4 border-t border-[#E4E1D8] flex items-center justify-between">
          <div className="text-[10px] text-[#8B8B8F]">
            <Sparkles className="inline h-3 w-3" /> LLM Sonnet 4.6 → GPT-5.2 → Haiku 4.5
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-[#5B5B62]">Annuler</button>
            <button onClick={gen} disabled={busy} className="px-4 py-1.5 text-sm bg-gradient-to-r from-[#C5A880] to-[#8B7355] text-white rounded-md disabled:opacity-50" data-testid="wizard-generate-btn">
              {busy ? "Génération IA (15-45s)…" : "🪄 Générer le document"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (<div><div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62] mb-1">{label}</div>{children}</div>);
}

// ---------------------------------------------------------------------------
// Document viewer
// ---------------------------------------------------------------------------
function DocViewer({ doc, onClose, onDownload, onDownloadFormat, onRefresh }) {
  const [regenerating, setRegenerating] = useState(false);
  const [showWL, setShowWL] = useState(false);
  const [formatMenuOpen, setFormatMenuOpen] = useState(false);
  const allFormats = [
    { id: "pdf", label: "PDF", desc: "Impression A4" },
    { id: "docx", label: "Word", desc: "Modification (.docx)" },
    { id: "pptx", label: "PowerPoint", desc: "Présentation (.pptx)" },
    { id: "xlsx", label: "Excel", desc: "Tableau (.xlsx)" },
    { id: "html", label: "HTML", desc: "Consultation web" },
    { id: "md", label: "Markdown", desc: "Usage IA" },
  ];
  const regenerate = async () => {
    setRegenerating(true);
    try {
      await api.post(`/studio/documents/${doc.id}/regenerate`, doc.source_context || {});
      toast.success("Document régénéré");
      onRefresh();
    } catch { toast.error("Échec"); }
    finally { setRegenerating(false); }
  };
  const remove = async () => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await api.delete(`/studio/documents/${doc.id}`);
      toast.success("Supprimé");
      onRefresh();
    } catch { toast.error("Échec"); }
  };
  return (
    <div className="fixed inset-0 z-40 flex" data-testid="studio-viewer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full md:w-[720px] bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between z-10 gap-2 flex-wrap">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62]">
            {doc.label_fr} · généré par {doc.llm_used}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={() => setShowWL(true)} className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] bg-[#F1EFE9] text-[#0B2545] rounded" data-testid="studio-white-label-btn">
              <Palette className="h-3 w-3" /> White-label
            </button>
            <button onClick={regenerate} disabled={regenerating} className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] bg-[#F1EFE9] text-[#0B2545] rounded disabled:opacity-50" data-testid="studio-regenerate-btn">
              <RefreshCw className={`h-3 w-3 ${regenerating ? "animate-spin" : ""}`} /> Régénérer
            </button>
            <div className="relative">
              <button
                onClick={() => setFormatMenuOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#0B2545] text-white rounded-md hover:bg-[#123163]"
                data-testid="studio-download-btn"
              >
                <Download className="h-3.5 w-3.5" /> Télécharger
                <span className="ml-1 opacity-70">▾</span>
              </button>
              {formatMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-1 bg-white border border-[#E4E1D8] rounded-md shadow-lg z-30 min-w-[220px]"
                  data-testid="studio-format-menu"
                >
                  {allFormats.map((f) => (
                    <button
                      key={f.id}
                      data-testid={`studio-download-${f.id}`}
                      onClick={() => { setFormatMenuOpen(false); onDownloadFormat(doc, f.id); }}
                      className="w-full text-left px-3 py-2 hover:bg-[#F1EFE9] flex items-center justify-between gap-3 first:rounded-t-md last:rounded-b-md"
                    >
                      <div>
                        <div className="text-sm text-[#1F2937] font-medium">{f.label}</div>
                        <div className="text-[10px] text-[#8B8B8F]">{f.desc}</div>
                      </div>
                      <span className="text-[10px] font-mono text-[#8B8B8F] uppercase">.{f.id}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={remove} className="p-1.5 text-[#B45309] hover:text-[#7A3E00]" title="Supprimer">
              <Trash2 className="h-4 w-4" />
            </button>
            <button onClick={onClose} className="p-1 text-[#5B5B62] hover:text-[#1F2937]"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="px-6 py-6">
          {renderMarkdown(doc.content_md)}
          <div className="mt-8 pt-4 border-t border-[#E4E1D8] text-[11px] text-[#8B8B8F] flex items-center gap-2">
            <Clock className="h-3 w-3" /> {new Date(doc.created_at).toLocaleString("fr-CA")}
            {doc.versions?.length > 0 && <span>· {doc.versions.length} version(s) archivée(s)</span>}
          </div>
        </div>
      </div>
      {showWL && <WhiteLabelModal doc={doc} onClose={() => setShowWL(false)} onSaved={() => { setShowWL(false); onRefresh(); }} />}
    </div>
  );
}

function WhiteLabelModal({ doc, onClose, onSaved }) {
  const [form, setForm] = useState({
    org_name: doc.white_label?.org_name || "",
    primary_color: doc.white_label?.primary_color || "#0B2545",
    accent_color: doc.white_label?.accent_color || "#C5A880",
    header_text: doc.white_label?.header_text || "",
    footer_text: doc.white_label?.footer_text || "",
    watermark: doc.white_label?.watermark || "",
    logo_data_url: doc.white_label?.logo_data_url || "",
    signature_data_url: doc.white_label?.signature_data_url || "",
    qr_url: doc.white_label?.qr_url || "",
    qr_label: doc.white_label?.qr_label || "",
  });
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("brand"); // brand | assets | qr

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/studio/documents/${doc.id}/white-label`, form);
      toast.success("Personnalisation appliquée — regénérez le PDF pour la voir");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la sauvegarde");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full px-2.5 py-1.5 text-sm bg-white border border-[#E4E1D8] rounded-md focus:outline-none focus:border-[#0B2545]";

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error("Format non supporté (PNG, JPG ou WebP uniquement)");
      return;
    }
    if (file.size > 500 * 1024) {
      toast.error("Logo trop lourd (max 500 KB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_data_url: reader.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="whitelabel-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[92vh] flex flex-col">
        <div className="border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-lg font-display text-[#1F2937]">White-label & Identité</h3>
            <p className="text-[11px] text-[#8B8B8F] mt-0.5">Personnalisez chaque document exporté sous votre marque</p>
          </div>
          <button onClick={onClose} className="p-1 text-[#5B5B62] hover:text-[#1F2937]" data-testid="whitelabel-close-btn"><X className="h-5 w-5" /></button>
        </div>

        {/* Tabs */}
        <div className="border-b border-[#E4E1D8] px-6 flex gap-1 shrink-0">
          {[
            { k: "brand", label: "Marque" },
            { k: "assets", label: "Logo & signature" },
            { k: "qr", label: "QR code" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`whitelabel-tab-${t.k}`}
              className={`px-3 py-2.5 text-xs font-mono uppercase tracking-widest border-b-2 transition ${
                tab === t.k ? "border-[#0B2545] text-[#0B2545]" : "border-transparent text-[#5B5B62] hover:text-[#1F2937]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="px-6 py-4 space-y-3 overflow-y-auto flex-1">
          {tab === "brand" && (
            <>
              <Field label="Organisation">
                <input data-testid="whitelabel-org" className={inputCls} value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} placeholder="ex : Universitas Institut" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Couleur primaire">
                  <input type="color" data-testid="whitelabel-primary" className="w-full h-9 rounded border border-[#E4E1D8] cursor-pointer" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} />
                </Field>
                <Field label="Couleur accent">
                  <input type="color" data-testid="whitelabel-accent" className="w-full h-9 rounded border border-[#E4E1D8] cursor-pointer" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} />
                </Field>
              </div>
              <Field label="En-tête PDF (haut de chaque page)">
                <input data-testid="whitelabel-header" className={inputCls} value={form.header_text} onChange={(e) => setForm({ ...form, header_text: e.target.value })} placeholder="ex : Alpha Incubator × ALTIORA" />
              </Field>
              <Field label="Pied de page PDF">
                <input data-testid="whitelabel-footer" className={inputCls} value={form.footer_text} onChange={(e) => setForm({ ...form, footer_text: e.target.value })} placeholder="ex : hello@universitas.com · confidentiel" />
              </Field>
              <Field label="Filigrane (watermark en diagonale)">
                <input data-testid="whitelabel-watermark" className={inputCls} value={form.watermark} onChange={(e) => setForm({ ...form, watermark: e.target.value })} placeholder="CONFIDENTIEL · DRAFT · v2…" />
              </Field>
            </>
          )}

          {tab === "assets" && (
            <>
              <Field label="Logo (PNG/JPG/WebP · max 500 KB · hauteur ~120px optimale)">
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center gap-2 rounded-md border border-[#0B2545] bg-white text-[#0B2545] hover:bg-[#0B2545] hover:text-white text-xs font-medium px-3 py-2 transition">
                    <Upload className="h-3.5 w-3.5" />
                    <span>Choisir un logo</span>
                    <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoUpload} className="hidden" data-testid="whitelabel-logo-input" />
                  </label>
                  {form.logo_data_url && (
                    <button
                      onClick={() => setForm((f) => ({ ...f, logo_data_url: "" }))}
                      data-testid="whitelabel-logo-clear"
                      className="text-xs text-[#DC2626] hover:underline"
                    >
                      Retirer
                    </button>
                  )}
                </div>
                {form.logo_data_url && (
                  <div className="mt-3 p-3 rounded-md border border-[#E4E1D8] bg-[#F8F7F3] flex items-center justify-center">
                    <img src={form.logo_data_url} alt="logo preview" className="max-h-16 object-contain" data-testid="whitelabel-logo-preview" />
                  </div>
                )}
              </Field>

              <Field label="Signature manuscrite (dessinez avec la souris)">
                <SignaturePad
                  value={form.signature_data_url}
                  onChange={(v) => setForm((f) => ({ ...f, signature_data_url: v }))}
                />
              </Field>
            </>
          )}

          {tab === "qr" && (
            <>
              <div className="text-xs text-[#5B5B62] leading-relaxed mb-2">
                Un QR code est ajouté en bas à droite de chaque PDF/Word — pratique pour renvoyer vers votre site, un profil LinkedIn ou un formulaire.
              </div>
              <Field label="URL à encoder">
                <input
                  data-testid="whitelabel-qr-url"
                  className={inputCls}
                  value={form.qr_url}
                  onChange={(e) => setForm({ ...form, qr_url: e.target.value })}
                  placeholder="https://universitas.com/inscription"
                />
              </Field>
              <Field label="Libellé sous le QR (optionnel)">
                <input
                  data-testid="whitelabel-qr-label"
                  className={inputCls}
                  value={form.qr_label}
                  onChange={(e) => setForm({ ...form, qr_label: e.target.value })}
                  placeholder="ex : Postuler · S'inscrire"
                  maxLength={60}
                />
              </Field>
              {form.qr_url && (
                <div className="mt-2 rounded-md border border-[#E4E1D8] bg-[#F8F7F3] px-3 py-3 text-[11px] font-mono text-[#5B5B62] break-all">
                  Le QR pointera vers : <span className="text-[#1F2937]">{form.qr_url}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E4E1D8] flex items-center justify-between shrink-0">
          <div className="text-[11px] text-[#8B8B8F]">Les modifications s&apos;appliquent aux prochains exports.</div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-[#5B5B62] hover:text-[#1F2937]">Annuler</button>
            <button onClick={save} disabled={busy} data-testid="whitelabel-save-btn" className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[#0B2545] hover:bg-[#123163] text-white rounded-md disabled:opacity-50">
              {busy ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SignaturePad({ value, onChange }) {
  const canvasRef = React.useRef(null);
  const drawingRef = React.useRef(false);
  const lastRef = React.useRef({ x: 0, y: 0 });

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = value;
    }
  }, [value]);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return {
      x: ((point.clientX - rect.left) / rect.width) * canvas.width,
      y: ((point.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const start = (e) => {
    drawingRef.current = true;
    lastRef.current = pos(e);
  };
  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#1F2937";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  };
  const stop = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  return (
    <div>
      <div className="rounded-md border-2 border-dashed border-[#E4E1D8] bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          width={560}
          height={180}
          data-testid="whitelabel-signature-canvas"
          className="w-full h-[180px] cursor-crosshair touch-none"
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={stop}
        />
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px]">
        <span className="text-[#8B8B8F]">Dessinez votre signature — elle sera insérée en bas de chaque document.</span>
        <button onClick={clear} data-testid="whitelabel-signature-clear" className="text-[#DC2626] hover:underline">Effacer</button>
      </div>
    </div>
  );
}
